import React from 'react';
import { Clock } from 'lucide-react';

const CATEGORIES = [
  { key: 'weekdays', label: 'Monday - Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
  { key: 'public_holidays', label: 'Public Holidays' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

const DEFAULT_HOURS = {
  weekdays: { open_hour: '08', open_min: '00', close_hour: '17', close_min: '00', closed: false },
  saturday: { open_hour: '09', open_min: '00', close_hour: '13', close_min: '00', closed: false },
  sunday: { open_hour: '', open_min: '00', close_hour: '', close_min: '00', closed: true },
  public_holidays: { open_hour: '', open_min: '00', close_hour: '', close_min: '00', closed: true }
};

export function parseOpeningHours(hoursObj) {
  if (!hoursObj || typeof hoursObj !== 'object') return DEFAULT_HOURS;
  const result = { ...DEFAULT_HOURS };
  for (const cat of CATEGORIES) {
    const val = hoursObj[cat.key];
    if (!val || val === 'Closed') {
      result[cat.key] = { ...DEFAULT_HOURS[cat.key], closed: true };
    } else if (typeof val === 'string') {
      const parts = val.split(' - ');
      if (parts.length === 2) {
        const [oh, om] = parts[0].split(':');
        const [ch, cm] = parts[1].split(':');
        result[cat.key] = { open_hour: oh || '', open_min: om || '00', close_hour: ch || '', close_min: cm || '00', closed: false };
      }
    } else if (typeof val === 'object') {
      result[cat.key] = val;
    }
  }
  return result;
}

export function serializeOpeningHours(hours) {
  const result = {};
  for (const cat of CATEGORIES) {
    const h = hours[cat.key];
    if (h.closed) {
      result[cat.key] = 'Closed';
    } else {
      result[cat.key] = `${h.open_hour}:${h.open_min} - ${h.close_hour}:${h.close_min}`;
    }
  }
  return result;
}

export default function OpeningHoursEditor({ value, onChange }) {
  const hours = parseOpeningHours(value);

  const update = (catKey, field, val) => {
    const updated = {
      ...hours,
      [catKey]: { ...hours[catKey], [field]: val }
    };
    if (field === 'closed' && val) {
      updated[catKey] = { ...updated[catKey], open_hour: '', open_min: '00', close_hour: '', close_min: '00' };
    }
    if (field === 'closed' && !val) {
      const def = DEFAULT_HOURS[catKey];
      updated[catKey] = { ...updated[catKey], open_hour: def.open_hour || '08', open_min: '00', close_hour: def.close_hour || '17', close_min: '00' };
    }
    onChange(serializeOpeningHours(updated));
  };

  const formatLabel = (h, m) => {
    const hr = parseInt(h, 10);
    const suffix = hr >= 12 ? 'PM' : 'AM';
    const display = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    return `${display}:${m} ${suffix}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="h-5 w-5 text-[#4A90E2]" />
        <h3 className="font-manrope font-semibold text-lg text-[#0F2A44]">Opening Hours</h3>
      </div>

      {CATEGORIES.map(({ key, label }) => {
        const cat = hours[key];
        return (
          <div key={key} className="p-4 bg-slate-50 rounded-lg border border-slate-100" data-testid={`hours-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-sm text-[#0F2A44]">{label}</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-[#4B5563]">{cat.closed ? 'Closed' : 'Open'}</span>
                <div className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={!cat.closed}
                    onChange={(e) => update(key, 'closed', !e.target.checked)}
                    className="sr-only peer"
                    data-testid={`hours-${key}-toggle`}
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#2DD4BF]"></div>
                </div>
              </label>
            </div>

            {!cat.closed && (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <select
                    value={cat.open_hour}
                    onChange={(e) => update(key, 'open_hour', e.target.value)}
                    className="h-9 px-2 rounded border border-slate-200 text-sm bg-white"
                    data-testid={`hours-${key}-open-hour`}
                  >
                    {HOURS.map(h => (
                      <option key={h} value={h}>{parseInt(h, 10) === 0 ? '12 AM' : parseInt(h, 10) < 12 ? `${parseInt(h, 10)} AM` : parseInt(h, 10) === 12 ? '12 PM' : `${parseInt(h, 10) - 12} PM`}</option>
                    ))}
                  </select>
                  <span className="text-slate-400">:</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={cat.open_min}
                    onChange={(e) => {
                      let v = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                      update(key, 'open_min', v.toString().padStart(2, '0'));
                    }}
                    className="h-9 w-14 px-2 rounded border border-slate-200 text-sm text-center bg-white"
                    data-testid={`hours-${key}-open-min`}
                    placeholder="00"
                  />
                </div>

                <span className="text-sm text-[#4B5563] font-medium">to</span>

                <div className="flex items-center gap-1">
                  <select
                    value={cat.close_hour}
                    onChange={(e) => update(key, 'close_hour', e.target.value)}
                    className="h-9 px-2 rounded border border-slate-200 text-sm bg-white"
                    data-testid={`hours-${key}-close-hour`}
                  >
                    {HOURS.map(h => (
                      <option key={h} value={h}>{parseInt(h, 10) === 0 ? '12 AM' : parseInt(h, 10) < 12 ? `${parseInt(h, 10)} AM` : parseInt(h, 10) === 12 ? '12 PM' : `${parseInt(h, 10) - 12} PM`}</option>
                    ))}
                  </select>
                  <span className="text-slate-400">:</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={cat.close_min}
                    onChange={(e) => {
                      let v = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                      update(key, 'close_min', v.toString().padStart(2, '0'));
                    }}
                    className="h-9 w-14 px-2 rounded border border-slate-200 text-sm text-center bg-white"
                    data-testid={`hours-${key}-close-min`}
                    placeholder="00"
                  />
                </div>

                {cat.open_hour && cat.close_hour && (
                  <span className="text-xs text-[#2DD4BF] ml-2">
                    {formatLabel(cat.open_hour, cat.open_min)} - {formatLabel(cat.close_hour, cat.close_min)}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
